import { useState } from "react";
import { ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NativeOptionSheet, type NativeOption } from "@/components/native/NativeOptionSheet";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";

const SORT_VALUES = [
  "createdAt-desc",
  "price-asc",
  "price-desc",
  "year-desc",
  "year-asc",
  "mileage-asc",
  "mileage-desc",
  "make-asc",
  "make-desc",
  "endDate-desc",
  "endDate-asc",
] as const;

export type NativeListingsSearchSortProps = {
  sortBy: string;
  onSortChange: (value: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  hasActiveSearch?: boolean;
  onClearSearch?: () => void;
};

function sortLabel(
  value: string,
  labels: ReturnType<typeof auctionsLabels>,
): string {
  const map: Record<string, string> = {
    "createdAt-desc": labels.sort.createdAtDesc,
    "price-asc": labels.sort.priceAsc,
    "price-desc": labels.sort.priceDesc,
    "year-desc": labels.sort.yearDesc,
    "year-asc": labels.sort.yearAsc,
    "mileage-asc": labels.sort.mileageAsc,
    "mileage-desc": labels.sort.mileageDesc,
    "make-asc": labels.sort.makeAsc,
    "make-desc": labels.sort.makeDesc,
    "endDate-desc": labels.sort.endDateDesc,
    "endDate-asc": labels.sort.endDateAsc,
  };
  return map[value] ?? value;
}

/** Native listings: web-like search in FilterBar; floating sort chip when bar scrolls away. */
export function useNativeListingsSearchSort({
  sortBy,
  onSortChange,
}: Pick<NativeListingsSearchSortProps, "sortBy" | "onSortChange">) {
  const { language } = useLanguage();
  const labels = auctionsLabels(language);
  const [sortOpen, setSortOpen] = useState(false);

  const sortOptions: NativeOption[] = SORT_VALUES.map((v) => ({
    value: v,
    label: sortLabel(v, labels),
  }));

  const chipClass = (extra?: string) =>
    cn(
      "native-listings-fab-chip native-listings-fab-sort relative shadow-none hover:shadow-none pointer-events-auto",
      extra,
    );

  const renderSortButton = () => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={chipClass()}
      onClick={() => setSortOpen(true)}
    >
      <ArrowUpDown className="h-4 w-4 shrink-0" />
      {labels.sort.button}
    </Button>
  );

  const floatingSortControls = (visible: boolean) => (
    <div
      className={cn(
        "native-listings-action-fab native-listings-action-fab--sort-only pointer-events-none fixed z-[79] transition-all duration-300 ease-out",
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-3 pointer-events-none invisible",
      )}
      aria-hidden={!visible}
    >
      {renderSortButton()}
    </div>
  );

  const sheets = (
    <NativeOptionSheet
      open={sortOpen}
      onOpenChange={setSortOpen}
      title={labels.sort.placeholder}
      value={sortBy}
      options={sortOptions}
      onSelect={(v) => {
        onSortChange(v);
        setSortOpen(false);
      }}
    />
  );

  return { floatingSortControls, sheets, inlineSortButton: renderSortButton };
}
